import React, { useEffect, useState } from 'react'
import { Modal, Pagination, Dropdown, Menu, Button, message, Tag, Input } from 'antd'
import { useTranslation } from 'react-i18next'
import styles from './Users.module.less'
import { useAppDispatch, useAppSelector } from '~/Store'
import { RootState } from '~/Store/reducers'
import { UsersActions } from '~/Store/AdminUser'
import { getListUsers } from '~/Store/AdminUser/selectors'
import { UserResponse, AdminUserService } from '~/Services/AdminUsers'
import { SessionAdminService } from '~/Services/SessionAdminService'
import { getListRoles, getRolesToUserId } from '~/Store/Role/selectors'
import { RolesActions } from '~/Store/Role'
import { UserSelectors } from '~/Store/User'
import { RootClaim } from '../../Routes'
import { CaretDownOutlined, DeleteOutlined, MoreOutlined } from '@ant-design/icons'

// Khớp RevokeUserSessionsRequest.MaxReasonLength ở identity-service.
const REVOKE_REASON_MAX_LENGTH = 256

export const AdminUsers = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const usersResponse = useAppSelector(getListUsers)
  const users = usersResponse?.results ?? []
  const totalUsers = usersResponse?.queryCount ?? 0

  const rolesUserID = useAppSelector(getRolesToUserId)

  const [selectedUser, setSelectedUser] = useState<UserResponse | null>(null)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const roleAdminData = useAppSelector(getListRoles)
  const rolesUserIdLoadedFor = useAppSelector(
    (state: RootState) => state.roles.rolesUserIDUserId,
  )
  const [selectedRoles, setSelectedRoles] = useState<string[]>([])
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null)
  const [modalUserId, setModalUserId] = useState<string | null>(null)
  const [initialRolesLoaded, setInitialRolesLoaded] = useState(false)
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  // ID-08 — revoke sessions: endpoint chỉ nhận role System; ẩn menu với role khác chỉ là UX, 403 backend mới là chặn thật.
  const currentUserRoles = useAppSelector(UserSelectors.getRoles)
  const canRevokeSessions = currentUserRoles.includes(RootClaim.System)
  const [revokeTarget, setRevokeTarget] = useState<UserResponse | null>(null)
  const [revokeReason, setRevokeReason] = useState('')
  const [revoking, setRevoking] = useState(false)

  useEffect(() => {
    dispatch(RolesActions.getRolesRequest())
    dispatch(
      UsersActions.getUsersRequest({
        page: currentPage,
        pageSize: pageSize,
      }),
    )
  }, [dispatch, currentPage, pageSize])

  const getInvitationStatusColor = (status: string) => {
    switch (status) {
      case 'Pending':
        return 'orange'
      case 'Accepted':
        return 'green'
      case 'Rejected':
        return 'red'
      default:
        return 'gray'
    }
  }

  const getUserStatusTag = (isActive: boolean | undefined) => {
    if (isActive === false) {
      return <Tag color="red">{t('admin.blocked')}</Tag>
    }
    return <Tag color="green">{t('admin.active')}</Tag>
  }

  const showUserModal = (user: UserResponse) => {
    setSelectedUser(user)
    setModalUserId(user.id)
    setSelectedRoles([])
    setInitialRolesLoaded(false)
    dispatch(RolesActions.getUserRolesRequest(user.id))
    setIsModalVisible(true)
  }

  const handleCloseModal = () => {
    setIsModalVisible(false)
    setSelectedUser(null)
    setSelectedRoles([])
    setModalUserId(null)
    setInitialRolesLoaded(false)
  }

  const handleCheckboxChange = (rolename: string, checked: boolean) => {
    setSelectedRoles(prev => {
      if (checked) {
        if (!prev.includes(rolename)) {
          return [...prev, rolename]
        }
      } else {
        return prev.filter(name => name !== rolename)
      }
      return prev
    })
  }

  useEffect(() => {
    if (
      isModalVisible &&
      selectedUser?.id === modalUserId &&
      modalUserId === rolesUserIdLoadedFor &&
      !initialRolesLoaded
    ) {
      setSelectedRoles(rolesUserID)
      setInitialRolesLoaded(true)
    }
  }, [
    isModalVisible,
    selectedUser,
    modalUserId,
    rolesUserID,
    rolesUserIdLoadedFor,
    initialRolesLoaded,
  ])

  const handleOk = () => {
    if (!selectedUser) {
      handleCloseModal()
      return
    }

    const existingRoles =
      selectedUser.id === modalUserId &&
      modalUserId === rolesUserIdLoadedFor
        ? rolesUserID
        : []

    const rolesToAdd = selectedRoles.filter(
      role => !existingRoles.includes(role),
    )
    const rolesToRemove = existingRoles.filter(
      role => !selectedRoles.includes(role),
    )

    if (rolesToRemove.length > 0) {
      dispatch(
        RolesActions.deleteUserRolesRequest({
          userId: selectedUser.id,
          roleName: rolesToRemove,
        }),
      )
    }

    rolesToAdd.forEach(role => {
      dispatch(
        RolesActions.updateUserRoles({
          userId: selectedUser.id,
          roleName: role,
        }),
      )
    })

    handleCloseModal()
  }

  const handleCaretClick = (e: React.MouseEvent, user: UserResponse) => {
    e.stopPropagation()
    dispatch(RolesActions.getUserRolesRequest(user.id))
    setExpandedUserId(prev => (prev === user.id ? null : user.id))
  }

  const handlePageChange = (page: number, newPageSize?: number) => {
    setCurrentPage(page)
    if (newPageSize && newPageSize !== pageSize) {
      setPageSize(newPageSize)
    }
  }

  const openRevokeSessions = (user: UserResponse) => {
    setRevokeReason('')
    setRevokeTarget(user)
  }

  const closeRevokeSessions = () => {
    if (!revoking) setRevokeTarget(null)
  }

  const handleRevokeSessions = async () => {
    if (!revokeTarget) return
    setRevoking(true)
    try {
      await SessionAdminService.Post.revokeUserSessions(revokeTarget.id, revokeReason.trim() || undefined)
      message.success(t('admin.revokeSessionsSuccess'))
      setRevokeTarget(null)
    } catch (err: any) {
      console.error('Error revoking user sessions:', err)
      message.error(
        err?.response?.status === 403
          ? t('admin.revokeSessionsForbidden')
          : t('admin.revokeSessionsFailed'),
      )
    } finally {
      setRevoking(false)
    }
  }

  const handleBlockUser = async (user: UserResponse) => {
    try {
      const response = await AdminUserService.Put.blockUser(user.id)
      // ID-08: backend khoá user trước rồi mới revoke session (best-effort) — revoke lỗi thì user vẫn bị khoá,
      // báo admin thử lại bằng "Thu hồi phiên đăng nhập".
      if (response?.data?.revokeFailed?.length) {
        message.warning(t('admin.blockUserRevokeFailed'))
      } else {
        message.success(t('admin.blockUserSuccess'))
      }
      
      // Refresh user list
      dispatch(
        UsersActions.getUsersRequest({
          page: currentPage,
          pageSize: pageSize,
        }),
      )
    } catch (err) {
      console.error('Error blocking user:', err)
      message.error(t('admin.blockUserFailed'))
    }
  }

  const handleUnblockUser = async (user: UserResponse) => {
    try {
      await AdminUserService.Put.unblockUser(user.id)
      message.success(t('admin.unblockUserSuccess'))
      
      // Refresh user list
      dispatch(
        UsersActions.getUsersRequest({
          page: currentPage,
          pageSize: pageSize,
        }),
      )
    } catch (err) {
      console.error('Error unblocking user:', err)
      message.error(t('admin.unblockUserFailed'))
    }
  }

  return (
    <div className={styles.container}>
      <div className="table-container">
        {Array.isArray(users) && users.map(user => (
          <React.Fragment key={user.id}>
            <div
              className={`${styles.tableRow} ${
                expandedUserId === user.id ? styles.expanded : ''
              }`}
            >
              <div 
                className={styles.tableCell}
                onClick={() => showUserModal(user)}
                style={{ cursor: 'pointer' }}
              >
                {`${user.lastname ?? ''} ${user.firstname ?? ''}`}
              </div>
              <div 
                className={styles.tableCell}
                onClick={() => showUserModal(user)}
                style={{ cursor: 'pointer' }}
              >
                {user.email}
              </div>
              <div
                className={styles.tableCell}
                style={{ color: getInvitationStatusColor(user.invitationStatus) }}
                onClick={() => showUserModal(user)}
              >
                {user.invitationStatus || t('admin.unknown')}
              </div>
              <div
                className={styles.tableCell}
                onClick={() => showUserModal(user)}
                style={{ cursor: 'pointer' }}
              >
                {getUserStatusTag(user.isActive)}
              </div>
              <div
                onClick={e => handleCaretClick(e, user)}
                style={{ padding: '0 8px', cursor: 'pointer' }}
              >
                <CaretDownOutlined style={{ fontSize: 16 }} />
              </div>
              <div style={{ padding: '0 8px' }}>
                <Dropdown
                  overlay={
                    <Menu>
                      {user.isActive !== false ? (
                        <Menu.Item
                          key="block"
                          danger
                          onClick={(e) => {
                            e.domEvent.stopPropagation()
                            handleBlockUser(user)
                          }}
                        >
                          {t('admin.blockUser')}
                        </Menu.Item>
                      ) : (
                        <Menu.Item
                          key="unblock"
                          onClick={(e) => {
                            e.domEvent.stopPropagation()
                            handleUnblockUser(user)
                          }}
                        >
                          {t('admin.unblockUser')}
                        </Menu.Item>
                      )}
                      {canRevokeSessions && (
                        <Menu.Item
                          key="revokeSessions"
                          danger
                          data-testid={`admin-user-revoke-sessions-${user.id}`}
                          onClick={(e) => {
                            e.domEvent.stopPropagation()
                            openRevokeSessions(user)
                          }}
                        >
                          {t('admin.revokeSessions')}
                        </Menu.Item>
                      )}
                    </Menu>
                  }
                  trigger={['click']}
                >
                  <Button
                    type="link"
                    icon={<MoreOutlined />}
                    data-testid={`admin-user-actions-${user.id}`}
                    onClick={e => e.stopPropagation()}
                  />
                </Dropdown>
              </div>
            </div>

            {/* Roles dưới row */}
            {expandedUserId === user.id && (
              <div className={styles.rolesSection}>
                <strong>{t('admin.roles')}:</strong>
                <ul>
                  {Array.isArray(rolesUserID) && rolesUserID.length > 0 ? (
                    rolesUserID.map((role, idx) => (
                      <li key={idx} className={styles.roleItem}>
                        <span>{role}</span>
                        <DeleteOutlined
                          className={styles.deleteIcon}
                          onClick={() => {
                            Modal.confirm({
                              title: t('admin.confirmDelete'),
                              content: t('admin.confirmDeleteRole', { role }),
                              okText: t('admin.delete'),
                              okType: 'danger',
                              cancelText: t('admin.cancel'),
                              onOk() {
                                dispatch(
                                  RolesActions.deleteUserRolesRequest({
                                    userId: user.id,
                                    roleName: [role],
                                  }),
                                )
                              },
                            })
                          }}
                        />
                      </li>
                    ))
                  ) : (
                    <li className={styles.noRole}>{t('admin.noRole')}</li>
                  )}
                </ul>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Pagination */}
      <div style={{ textAlign: 'center', marginTop: '20px' }}>
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={totalUsers}
          onChange={handlePageChange}
          onShowSizeChange={handlePageChange}
          showSizeChanger
          pageSizeOptions={['10', '20', '50', '100']}
          showTotal={(total, range) =>
            `${range[0]}-${range[1]} ${t('admin.of')} ${total} ${t('admin.users')}`
          }
        />
      </div>

      <Modal
        open={isModalVisible}
        onCancel={handleCloseModal}
        onOk={handleOk}
        title={t('admin.roleList')}
        centered
        width={600}
      >
        <div className={styles.roleModalWrapper}>
          <strong>{t('admin.selectRole')}:</strong>
          <div className={styles.checkboxGroup}>
            {Array.isArray(roleAdminData) && roleAdminData.map(role => (
              <div key={role.name} className={styles.checkboxItem}>
                <label>
                  <input
                    type="checkbox"
                    value={role.name}
                    checked={selectedRoles.includes(role.name)}
                    onChange={e =>
                      handleCheckboxChange(role.name, e.target.checked)
                    }
                  />
                  {role.name}
                </label>
              </div>
            ))}
          </div>
          <p className={styles.roleNote}>* {t('admin.onlyOneRole')}</p>
        </div>
      </Modal>

      <Modal
        open={!!revokeTarget}
        onCancel={closeRevokeSessions}
        onOk={handleRevokeSessions}
        title={t('admin.revokeSessionsConfirmTitle')}
        okText={t('admin.revokeSessions')}
        okButtonProps={{ danger: true, loading: revoking, 'data-testid': 'admin-user-revoke-sessions-confirm' }}
        cancelText={t('admin.cancel')}
        centered
        destroyOnClose
      >
        <p>{t('admin.revokeSessionsConfirmBody', { email: revokeTarget?.email ?? '' })}</p>
        <Input.TextArea
          data-testid="admin-user-revoke-sessions-reason"
          placeholder={t('admin.revokeSessionsReason')}
          maxLength={REVOKE_REASON_MAX_LENGTH}
          showCount
          rows={3}
          value={revokeReason}
          onChange={e => setRevokeReason(e.target.value)}
        />
      </Modal>
    </div>
  )
}